import { ReactElement, useContext, useEffect, useState } from 'react';
import SchemaOrg from 'components/StructuredData/SchemaOrg';
import SiteDataContext from 'context/SiteDataContext';
import Link from 'components/UI/Link';
import NoResults from 'components/UI/NoResults';

import styles from './styles.module.css';
import { Job } from 'lib/greenhouse/api';
import { useRouter } from 'next/router';
import { firstValue } from 'lib/routing';
import { jobSchema } from 'lib/greenhouse/schema';
import { toJobSlug } from 'lib/greenhouse/url';

export interface GreenhouseProps {
  boardId: string;
  jobs: Job[];
}

export default function Greenhouse({ jobs, boardId }: GreenhouseProps): ReactElement {
  const { domain } = useContext(SiteDataContext);
  const router = useRouter();
  const [filteredJobs, setFilteredJobs] = useState(jobs);
  const [department, setDepartment] = useState(firstValue(router.query.department) || '');
  const [state, setState] = useState(firstValue(router.query.state) || '');

  useEffect((): void => {
    setFilteredJobs(
      jobs.filter(({ state: jobState, department: jobDept }) => {
        const show =
          (department === '' || jobDept === department) && (state === '' || jobState === state);
        return show;
      })
    );
  }, [department, state, jobs]);

  useEffect((): void => {
    if (state !== '') {
      router.query.state = state;
    } else {
      delete router.query.state;
    }
    if (department !== '') {
      router.query.department = department;
    } else {
      delete router.query.department;
    }
    router.push(router, undefined, { shallow: true });
  }, [state, department]);

  const renderJob = (job: Job): ReactElement => (
    <li
      className="flex flex-col px-3 py-5 hover:shadow-2xl lg:flex-row lg:justify-between  lg:px-5 lg:py-8 text-lg"
      key={job.id}
    >
      <Link {...toJobSlug(job, boardId)}>
        <a className="font-bold text-teal-600">{job.title}</a>
      </Link>
      <span className="font-medium">{job.location}</span>
    </li>
  );

  // render department options based on unique department names
  // from jobs array
  const renderDepartments = (): ReactElement[] =>
    jobs
      .reduce(
        (unq: Array<string>, job: Job) => [
          ...unq,
          ...(!unq.includes(job.department) ? [job.department] : []),
        ],
        []
      )
      .map((option) => (
        <option value={option} key={`department-${option}`}>
          {option}
        </option>
      ));

  // render unique state options based on job locations
  const renderStates = (): ReactElement[] =>
    jobs
      .reduce(
        (unq: Array<string>, job: Job) => [
          ...unq,
          ...(!unq.includes(job.state) ? [job.state] : []),
        ],
        []
      )
      .sort()
      .map((option) => (
        <option value={option} key={`state-${option}`}>
          {option}
        </option>
      ));

  return (
    <>
      <SchemaOrg
        json={{
          '@context': 'https://schema.org',
          '@graph': jobs.map((job) => jobSchema(domain, job)),
        }}
      />
      <div className="flex flex-col mb-6 px-site-component lg:mb-10">
        <h2 className="text-2xl my-3 lg:text-3xl lg:my-5">Current Job Openings</h2>
        <div className="flex flex-col my-3 lg:flex-row lg:my-5">
          <select
            className="border border-gray-500 mb-5 rounded p-4 lg:mb-0 lg:mr-10"
            value={department}
            onChange={({ target: { value } }) => setDepartment(value)}
          >
            <option value="">All Departments</option>
            {renderDepartments()}
          </select>
          <select
            className="border border-gray-500 rounded p-4"
            value={state}
            onChange={({ target: { value } }) => setState(value)}
          >
            <option value="">All States</option>
            {renderStates()}
          </select>
        </div>
        <div className={styles.jobs}>
          <ul className="my-3 lg:my-5">{filteredJobs.map(renderJob)}</ul>
          <div className="flex justify-items-center align-middle m-3 lg:justify-start lg:m-10 lg:ml-20">
            <NoResults />
            <p className="flex flex-col align-middle justify-center pl-4">
              No Results Found.
              <br />
              <b>Check Back Soon!</b>
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
